import { useMemo } from 'react';
import { useAppSelector } from 'app/store';
import { selectAdminUser } from 'features/auth/authSlice';
import { UserDiscriminator } from 'features/auth/authTypes';
import { Permission, Role, ALL_PERMISSIONS } from 'features/roles';
import { useGetRolesQuery } from 'features/roles/rolesApi';

export interface UsePermissionsResult {
  /** Current user's permissions array */
  permissions: Permission[];
  /** Current user's role object (if fetched) */
  role: Role | null;
  /** Check if user has a specific permission */
  hasPermission: (permission: Permission) => boolean;
  /** Check if user has ANY of the given permissions */
  hasAnyPermission: (permissions: Permission[]) => boolean;
  /** Check if user has ALL of the given permissions */
  hasAllPermissions: (permissions: Permission[]) => boolean;
  /**
   * Simplified permission check that includes superAdmin bypass.
   * Use this instead of: isSuperAdmin || hasPermission(...)
   *
   * @param permission - Single permission or array of permissions
   * @param mode - 'ALL' (default) requires all permissions, 'ANY' requires at least one
   * @returns True if user can perform the action
   *
   * @example
   * const { can } = usePermissions();
   * const canEdit = can(Permission.EDIT_PROJECTS);
   * const canManage = can([Permission.CREATE_PROJECTS, Permission.EDIT_PROJECTS], 'ANY');
   */
  can: (permission: Permission | Permission[], mode?: 'ALL' | 'ANY') => boolean;
  /** Whether the current user is a superAdmin (app-level global admin, set manually in DB) */
  isSuperAdmin: boolean;
  /** Whether the current user has admin-level permissions (MANAGE_USERS + MANAGE_PERMISSIONS) */
  isAdmin: boolean;
  /** Loading state while fetching roles */
  isLoading: boolean;
}

/**
 * Hook to check current user's permissions.
 * superAdmin (app-level) automatically has all permissions.
 * Other users get permissions from their assigned roleRef.
 *
 * The login endpoint now returns permissions[] from the user's roleRef,
 * so we use that when available to avoid extra API calls.
 */
export const usePermissions = (): UsePermissionsResult => {
  // Use Redux selector instead of localStorage
  const user = useAppSelector(selectAdminUser);

  // Only regular users have roles
  const isRegularUser = user?.discriminator === UserDiscriminator.REGULAR;

  // Check superAdmin field (app-level global admin, set manually in DB)
  const isSuperAdmin = isRegularUser && user?.superAdmin === true;

  // Check if user already has permissions from login response
  // Permissions are now included in the login response and stored in Redux
  const serverPermissions = isRegularUser ? user?.permissions : undefined;
  const hasServerPermissions = serverPermissions && serverPermissions.length > 0;

  // Fetch all roles to find the user's role permissions
  // Skip if no user, if superAdmin (they have all permissions anyway),
  // or if we already have permissions from the server (login response)
  const shouldSkip = !isRegularUser || isSuperAdmin || !user?._id || hasServerPermissions;
  const { data: roles, isLoading } = useGetRolesQuery(undefined, {
    skip: shouldSkip,
  });

  // Get roleRef from user
  const roleRef = isRegularUser && 'roleRef' in (user as unknown as Record<string, unknown>)
    ? (user as unknown as Record<string, unknown>).roleRef as string
    : undefined;

  // Find the user's role from fetched roles
  const role = useMemo(() => {
    if (!roles || isSuperAdmin) return null;

    // Find role by ID from roleRef
    if (roleRef) {
      return roles.find((r) => r._id === roleRef) || null;
    }

    return null;
  }, [roles, roleRef, isSuperAdmin]);

  // Calculate permissions
  const permissions = useMemo(() => {
    // superAdmin (app-level) gets all permissions
    if (isSuperAdmin) {
      return ALL_PERMISSIONS;
    }

    // Use server-provided permissions if available (from login response)
    if (hasServerPermissions) {
      return serverPermissions as Permission[];
    }

    // Fallback: Get permissions from user's role
    if (role) {
      return role.permissions;
    }

    // No permissions by default
    return [];
  }, [isSuperAdmin, hasServerPermissions, serverPermissions, role]);

  // Check if user has admin-level permissions
  const isAdmin = useMemo(() => {
    if (isSuperAdmin) return true;
    return permissions.includes(Permission.MANAGE_USERS) && permissions.includes(Permission.MANAGE_PERMISSIONS);
  }, [isSuperAdmin, permissions]);

  // Permission check functions
  // Note: superAdmin (app-level) gets all permissions via the permissions array above
  const hasPermission = useMemo(
    () => (permission: Permission): boolean => {
      if (isSuperAdmin) return true;
      return permissions.includes(permission);
    },
    [permissions, isSuperAdmin],
  );

  const hasAnyPermission = useMemo(
    () => (perms: Permission[]): boolean => {
      if (isSuperAdmin) return true;
      return perms.some((p) => permissions.includes(p));
    },
    [permissions, isSuperAdmin],
  );

  const hasAllPermissions = useMemo(
    () => (perms: Permission[]): boolean => {
      if (isSuperAdmin) return true;
      return perms.every((p) => permissions.includes(p));
    },
    [permissions, isSuperAdmin],
  );

  /**
   * Simplified permission check that includes superAdmin/admin bypass.
   * Replaces the common pattern: isSuperAdmin || isAdmin || hasPermission(...)
   */
  const can = useMemo(
    () =>
      (permission: Permission | Permission[], mode: 'ALL' | 'ANY' = 'ALL'): boolean => {
        // SuperAdmin and Admin always have access
        if (isSuperAdmin || isAdmin) return true;

        // Handle single permission
        if (!Array.isArray(permission)) {
          return permissions.includes(permission);
        }

        // Handle empty array - no permissions required
        if (permission.length === 0) return true;

        // Handle array based on mode
        return mode === 'ALL'
          ? permission.every((p) => permissions.includes(p))
          : permission.some((p) => permissions.includes(p));
      },
    [permissions, isSuperAdmin, isAdmin],
  );

  return {
    permissions,
    role,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    isSuperAdmin,
    isAdmin,
    isLoading,
  };
};

export default usePermissions;
